export interface BigInteger {
    signum?: number;
    mag?: number[];
}

export interface LegacyBigDecimal {
    intVal?: BigInteger;
    scale?: number;
}

// OpenAPI now models decimal-like values as strings in many schemas (for example ReactionAmount.massValue).
// Keep a string canonical type while still accepting legacy object-shaped decimals.
export type BigDecimal = string;

const BASE = 2 ** 32;

const isLegacyBigDecimal = (value: unknown): value is LegacyBigDecimal =>
    typeof value === "object" && value !== null && "intVal" in value;

const legacyBigDecimalToString = (value: LegacyBigDecimal): string => {
    const intVal = value.intVal;
    if (!intVal || !intVal.mag || intVal.mag.length === 0) {
        return "0";
    }

    const sign = intVal.signum && intVal.signum < 0 ? "-" : "";
    let magnitude = 0;
    for (const part of intVal.mag) {
        magnitude = magnitude * BASE + (part >>> 0);
    }

    const digits = Math.trunc(magnitude).toString();
    const scale = value.scale ?? 0;

    if (scale <= 0) {
        return `${sign}${digits}${scale < 0 ? "0".repeat(-scale) : ""}`;
    }

    if (digits.length <= scale) {
        const padded = digits.padStart(scale, "0");
        return `${sign}0.${padded}`;
    }

    const integerPart = digits.slice(0, digits.length - scale);
    const fractionalPart = digits.slice(digits.length - scale);
    return `${sign}${integerPart}.${fractionalPart}`;
};

const normalizeDecimalString = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric)) return undefined;

    return trimmed;
};

export const toBigDecimal = (
    value: string | number | LegacyBigDecimal | null | undefined
): BigDecimal | undefined => {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    if (isLegacyBigDecimal(value)) {
        return legacyBigDecimalToString(value);
    }

    if (typeof value === "number") {
        if (!Number.isFinite(value)) return undefined;
        return value.toString();
    }

    return normalizeDecimalString(value);
};

export const bigDecimalToString = (
    value: BigDecimal | number | LegacyBigDecimal | null | undefined
): string => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "number") {
        return Number.isFinite(value) ? value.toString() : "";
    }

    if (typeof value === "string") {
        return value;
    }

    return legacyBigDecimalToString(value);
};

export const bigDecimalToNumber = (
    value: BigDecimal | number | LegacyBigDecimal | null | undefined
): number | undefined => {
    const asString = bigDecimalToString(value);
    if (!asString) {
        return undefined;
    }

    const parsed = Number(asString);
    return Number.isFinite(parsed) ? parsed : undefined;
};
